<script setup lang="ts">
import type { MenuItem } from '~~/types'

const props = defineProps<{
  item: MenuItem
}>()

// Determine if item has nested structure (provinces -> cities)
const hasNestedStructure = computed(() => {
  return props.item.children?.some(child => child.children && child.children.length > 0)
})

const layout = computed(() => {
  if (!props.item.children || props.item.children.length === 0) {
    return 'none'
  }
  return props.item.layout || (hasNestedStructure.value ? 'two-column' : 'grid')
})
</script>

<template>
  <UContainer class="py-8">
    <div
      v-if="layout !== 'none'"
      class="grid"
      :class="layout === 'two-column' || item.featured ? 'grid-cols-12 gap-8' : 'grid-cols-4 gap-x-8 gap-y-3'"
    >
      <!-- Two-column layout: province groups -->
      <div
        v-if="layout === 'two-column'"
        class="col-span-9 grid grid-cols-3 gap-x-8 gap-y-6"
      >
        <NavMegaGroup
          v-for="group in item.children"
          :key="group.to"
          :title="group.label"
          :to="group.to"
          :links="group.children || []"
        />
      </div>

      <!-- Grid layout: simple link list -->
      <div
        v-else
        :class="item.featured ? 'col-span-9 grid grid-cols-3 gap-x-12 gap-y-3 content-start' : 'contents'"
      >
        <NuxtLink
          v-for="child in item.children"
          :key="child.to"
          :to="child.to"
          class="text-sm text-muted hover:text-highlighted transition-colors"
        >
          {{ child.label }}
        </NuxtLink>
      </div>

      <!-- Featured content (right sidebar) -->
      <div
        v-if="item.featured"
        class="col-span-3 border-l border-default pl-8"
      >
        <div class="flex flex-col gap-4">
          <!-- CTA Box -->
          <div
            v-if="item.featured.cta"
            class="bg-[var(--ui-bg-muted)] border border-default rounded-xl p-4 flex flex-col gap-3"
          >
            <p class="text-base font-semibold text-highlighted">
              {{ item.featured.cta.title }}
            </p>
            <UButton
              :to="item.featured.cta.to"
              color="primary"
              variant="solid"
              size="md"
              block
            >
              {{ item.featured.cta.label }}
            </UButton>
          </div>

          <!-- Info Links -->
          <div class="flex flex-col gap-3">
            <h3 class="text-sm font-semibold text-highlighted">
              {{ item.featured.title }}
            </h3>
            <ul class="space-y-3">
              <li
                v-for="link in item.featured.links"
                :key="link.to"
              >
                <NuxtLink
                  :to="link.to"
                  class="text-sm text-muted hover:text-highlighted transition-colors flex items-center gap-2"
                >
                  <UIcon
                    name="i-lucide-arrow-right"
                    class="w-4 h-4"
                  />
                  {{ link.label }}
                </NuxtLink>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </UContainer>
</template>
